import React, { useEffect, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import indicadoresService from "services/IndicadoresService";
import { FloppyDisk, SignOut } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const COLORS = [
	"#22c55e", "#3b82f6", "#f97316", "#06b6d4", "#eab308",
	"#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b",
	"#6366f1", "#10b981",
];

const getRandomColor = () => COLORS[Math.floor(Math.random() * COLORS.length)];

const customStyles = {
	option: (provided, state) => ({
		...provided,
		color: state.isSelected ? "blue" : "black",
		backgroundColor: "white",
		"&:hover": { backgroundColor: "#556ee6" },
	}),
	menuPortal: (base) => ({ ...base, zIndex: 10050 }),
};

const typeOptions = [
	{ value: "0", label: "Meta" },
	{ value: "1", label: "Valor Apuração" },
	{ value: "2", label: "Customizada" },
];

const graphicType = [
	{ value: 0, label: "Não Exibir" },
	{ value: 1, label: "Coluna" },
	{ value: 2, label: "Linha" },
];

const totalizadorInfos = [
	{ value: "", label: "Nenhum" },
	{ value: "sum", label: "Soma" },
	{ value: "average", label: "Média" },
	{ value: "calc", label: "Cálculo" },
];

const defaultConfigForm = () => ({
	type: null,
	type_meta_definition: null,
	display_on_chart: null,
	color: null,
	totalizator: null,
	totalizator_ref: null,
});

const AddVariableModal = ({
	isOpen,
	onClose,
	goalId,
	existingVariableIds,
	existingConfigs = [],
	onVariableAdded,
	indicatorPeriodicity,
}) => {
	const [variables, setVariables] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [selectedVariable, setSelectedVariable] = useState(null);
	const [configForm, setConfigForm] = useState(defaultConfigForm());
	const [touched, setTouched] = useState({});

	useEffect(() => {
		if (isOpen) {
			fetchVariables();
			setSelectedVariable(null);
			setConfigForm(defaultConfigForm());
			setTouched({});
		}
	}, [isOpen]);

	const fetchVariables = async () => {
		setLoading(true);
		try {
			const response = await indicadoresService.getVariables();
			let vars = response || [];

			if (indicatorPeriodicity) {
				vars = vars.filter(
					(variable) => variable.periodicity === indicatorPeriodicity,
				);
			}

			setVariables(vars);
		} catch (error) {
			console.error("Erro ao buscar variáveis:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectVariable = (variable) => {
		if (existingVariableIds.includes(variable.id)) return;
		setSelectedVariable(variable);
		setConfigForm(defaultConfigForm());
		setTouched({});
	};

	const handleBackToList = () => {
		setSelectedVariable(null);
		setConfigForm(defaultConfigForm());
		setTouched({});
	};

	const updateConfigForm = (field, value) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		setConfigForm((prev) => {
			const next = { ...prev, [field]: value };
			if (field === "type" && value !== "0") {
				next.type_meta_definition = null;
			}
			if (
				field === "totalizator" &&
				value !== "sum" &&
				value !== "average"
			) {
				next.totalizator_ref = null;
			}
			if (field === "type" && value === "1") {
				next.totalizator_ref = null;
			}
			return next;
		});
	};

	const getConfigName = (config) =>
		config?.GoalsVariable?.name || config?.name || "—";

	const referenceOptions = [
		{ value: "", label: "Nenhum" },
		...existingConfigs
			.filter((c) => String(c.type) === "1")
			.map((c) => ({
				value: c.id,
				label: getConfigName(c),
			})),
	];

	const isReferenceDisabled =
		configForm.type === null ||
		String(configForm.type) === "1" ||
		configForm.totalizator === null ||
		(configForm.totalizator !== "sum" &&
			configForm.totalizator !== "average");

	const getValidationErrors = () => {
		const errors = {};
		if (configForm.type === null || configForm.type === undefined) {
			errors.type = "Selecione o tipo";
		}
		if (configForm.type === "0" && !configForm.type_meta_definition) {
			errors.type_meta_definition =
				"Selecione Maior Melhor ou Menor Melhor";
		}
		if (
			configForm.display_on_chart === null ||
			configForm.display_on_chart === undefined
		) {
			errors.display_on_chart = "Selecione como exibir no gráfico";
		}
		if (!configForm.color) {
			errors.color = "Selecione a cor";
		}
		if (
			configForm.totalizator === null ||
			configForm.totalizator === undefined
		) {
			errors.totalizator = "Selecione o totalizador";
		}
		return errors;
	};

	const validationErrors = getValidationErrors();
	const isFormValid = Object.keys(validationErrors).length === 0;

	const handleSave = async () => {
		if (!selectedVariable) return;

		setTouched({
			type: true,
			type_meta_definition: true,
			display_on_chart: true,
			color: true,
			totalizator: true,
		});

		const errors = getValidationErrors();
		if (Object.keys(errors).length > 0) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="warning-icon"></i>',
				title: "Campos obrigatórios",
				text: "Preencha todos os campos obrigatórios antes de salvar.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
			return;
		}

		setSaving(true);
		try {
			const nextSequence = (existingConfigs.length || 0) + 1;
			const payload = {
				type: configForm.type,
				type_meta_definition:
					configForm.type === "0"
						? configForm.type_meta_definition
						: null,
				display_on_chart: configForm.display_on_chart,
				sequence: nextSequence,
				color: configForm.color || getRandomColor(),
				totalizator: configForm.totalizator || null,
				totalizator_ref: isReferenceDisabled
					? null
					: configForm.totalizator_ref || null,
			};

			if (!goalId) {
				const localConfig = {
					id: "temp_" + Date.now() + "_" + selectedVariable.id,
					goals_variables_id: selectedVariable.id,
					GoalsVariable: {
						id: selectedVariable.id,
						name: selectedVariable.name,
						quantifier: selectedVariable.quantifier,
						periodicity: selectedVariable.periodicity,
						decimals_cases: selectedVariable.decimals_cases,
					},
					formula: null,
					...payload,
				};
				onVariableAdded(localConfig);
			} else {
				const result = await indicadoresService.addVariableConfig({
					goals_id: goalId,
					goals_variables_id: selectedVariable.id,
					...payload,
				});
				onVariableAdded(result);
			}

			handleClose();
		} catch (error) {
			console.error("Erro ao adicionar variável:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Não foi possível adicionar a variável ao indicador.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		setSelectedVariable(null);
		setConfigForm(defaultConfigForm());
		setTouched({});
		onClose();
	};

	const fieldErrorStyle = { fontSize: 11, color: "#dc3545", marginTop: 4 };

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			size="lg"
			centered
			style={{ minWidth: "800px" }}
		>
			<div
				className="modal-header"
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
				}}
			>
				<div>
					<h5 style={{ marginBottom: 4 }}>
						{selectedVariable
							? "Configurar variável no indicador"
							: "Adicionar Variável ao Indicador"}
					</h5>
					<div style={{ fontSize: 13, color: "#6c757d" }}>
						{selectedVariable ? (
							<>
								Configure como{" "}
								<strong>{selectedVariable.name}</strong> entra
								neste indicador e salve para adicionar.
							</>
						) : (
							<>
								Selecione uma variável existente da lista.
								<br />
								<span style={{ fontSize: 12 }}>
									Somente variáveis com a mesma periodicidade
									selecionada no indicador são exibidas aqui.
								</span>
							</>
						)}
					</div>
				</div>
				<button
					type="button"
					className="btn-close"
					onClick={handleClose}
					aria-label="Close"
				/>
			</div>
			<ModalBody style={{ padding: "20px 24px" }}>
				{selectedVariable ? (
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						<div
							style={{
								padding: "10px 12px",
								backgroundColor: "#f8f9fa",
								border: "1px solid #dee2e6",
								borderRadius: 6,
								fontSize: 13,
							}}
						>
							<strong>{selectedVariable.name}</strong>
							<span style={{ color: "#6c757d", marginLeft: 8 }}>
								{selectedVariable.quantifier || "Nenhum"} ·{" "}
								{selectedVariable.periodicity}
							</span>
						</div>

						{/* Linha 1: Cor + Tipo (+ Definição da Meta se tipo Meta) */}
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 12,
								alignItems: "flex-start",
							}}
						>
							<div>
								<div
									style={{
										fontSize: 11,
										color: "#6c757d",
										marginBottom: 2,
									}}
								>
									Cor <span className="text-danger">*</span>
								</div>
								<Input
									type="color"
									value={configForm.color || "#ffffff"}
									onChange={(e) =>
										updateConfigForm("color", e.target.value)
									}
									style={{
										width: 36,
										height: 36,
										padding: 2,
										border:
											touched.color && validationErrors.color
												? "1px solid #dc3545"
												: "1px solid #dee2e6",
										borderRadius: 4,
										cursor: "pointer",
										opacity: configForm.color ? 1 : 0.45,
									}}
								/>
								{touched.color && validationErrors.color && (
									<div style={fieldErrorStyle}>
										{validationErrors.color}
									</div>
								)}
							</div>

							<div style={{ minWidth: 200, flex: 1 }}>
								<div
									style={{
										fontSize: 11,
										color: "#6c757d",
										marginBottom: 2,
									}}
								>
									Tipo <span className="text-danger">*</span>
								</div>
								<Select
									styles={customStyles}
									placeholder="Selecione..."
									value={
										typeOptions.find(
											(o) => o.value === configForm.type,
										) || null
									}
									onChange={(opt) =>
										updateConfigForm("type", opt.value)
									}
									options={typeOptions}
									menuPortalTarget={document.body}
									menuPosition="fixed"
								/>
								{touched.type && validationErrors.type && (
									<div style={fieldErrorStyle}>
										{validationErrors.type}
									</div>
								)}
							</div>

							{configForm.type === "0" && (
								<div style={{ minWidth: 220, flex: 1 }}>
									<div
										style={{
											fontSize: 11,
											color: "#6c757d",
											marginBottom: 2,
										}}
									>
										Definição da Meta{" "}
										<span className="text-danger">*</span>
									</div>
									<div
										style={{
											display: "flex",
											alignItems: "stretch",
											height: 38,
											borderRadius: 4,
											overflow: "hidden",
											border:
												touched.type_meta_definition &&
												validationErrors.type_meta_definition
													? "1px solid #dc3545"
													: "1px solid #dee2e6",
										}}
									>
										<button
											type="button"
											onClick={() =>
												updateConfigForm(
													"type_meta_definition",
													"maior_melhor",
												)
											}
											style={{
												flex: 1,
												border: "none",
												outline: "none",
												margin: 0,
												padding: "6px 8px",
												fontSize: 11,
												fontWeight: 600,
												cursor: "pointer",
												whiteSpace: "nowrap",
												backgroundColor:
													configForm.type_meta_definition ===
													"maior_melhor"
														? "#212529"
														: "white",
												color:
													configForm.type_meta_definition ===
													"maior_melhor"
														? "white"
														: "#6c757d",
											}}
										>
											Maior Melhor
										</button>
										<button
											type="button"
											onClick={() =>
												updateConfigForm(
													"type_meta_definition",
													"menor_melhor",
												)
											}
											style={{
												flex: 1,
												border: "none",
												borderLeft: "1px solid #dee2e6",
												outline: "none",
												margin: 0,
												padding: "6px 8px",
												fontSize: 11,
												fontWeight: 600,
												cursor: "pointer",
												whiteSpace: "nowrap",
												backgroundColor:
													configForm.type_meta_definition ===
													"menor_melhor"
														? "#212529"
														: "white",
												color:
													configForm.type_meta_definition ===
													"menor_melhor"
														? "white"
														: "#6c757d",
											}}
										>
											Menor Melhor
										</button>
									</div>
									{touched.type_meta_definition &&
										validationErrors.type_meta_definition && (
											<div style={fieldErrorStyle}>
												{
													validationErrors.type_meta_definition
												}
											</div>
										)}
								</div>
							)}
						</div>

						{/* Linha 2: Exibir no Gráfico */}
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 12,
								alignItems: "flex-start",
							}}
						>
							<div style={{ minWidth: 200, flex: 1 }}>
								<div
									style={{
										fontSize: 11,
										color: "#6c757d",
										marginBottom: 2,
									}}
								>
									Exibir no Gráfico{" "}
									<span className="text-danger">*</span>
								</div>
								<Select
									styles={customStyles}
									placeholder="Selecione..."
									value={
										graphicType.find(
											(o) =>
												o.value ===
												configForm.display_on_chart,
										) || null
									}
									onChange={(opt) =>
										updateConfigForm(
											"display_on_chart",
											opt.value,
										)
									}
									options={graphicType}
									menuPortalTarget={document.body}
									menuPosition="fixed"
								/>
								{touched.display_on_chart &&
									validationErrors.display_on_chart && (
										<div style={fieldErrorStyle}>
											{validationErrors.display_on_chart}
										</div>
									)}
							</div>
						</div>

						{/* Linha 3: Totalizador + Coluna Referência */}
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 12,
								alignItems: "flex-start",
							}}
						>
							<div style={{ minWidth: 160, flex: 1 }}>
								<div
									style={{
										fontSize: 11,
										color: "#6c757d",
										marginBottom: 2,
									}}
								>
									Totalizador{" "}
									<span className="text-danger">*</span>
								</div>
								<Select
									styles={customStyles}
									placeholder="Selecione..."
									value={
										configForm.totalizator === null
											? null
											: totalizadorInfos.find(
													(o) =>
														o.value ===
														configForm.totalizator,
												) || null
									}
									onChange={(opt) =>
										updateConfigForm(
											"totalizator",
											opt.value,
										)
									}
									options={totalizadorInfos}
									menuPortalTarget={document.body}
									menuPosition="fixed"
								/>
								{touched.totalizator &&
									validationErrors.totalizator && (
										<div style={fieldErrorStyle}>
											{validationErrors.totalizator}
										</div>
									)}
							</div>

							<div style={{ minWidth: 180, flex: 1 }}>
								<div
									style={{
										fontSize: 11,
										color: "#6c757d",
										marginBottom: 2,
									}}
								>
									Coluna Referência
								</div>
								<Select
									styles={customStyles}
									placeholder="Selecione..."
									isDisabled={isReferenceDisabled}
									value={
										isReferenceDisabled
											? null
											: referenceOptions.find(
													(o) =>
														o.value ===
															configForm.totalizator_ref ||
														String(o.value) ===
															String(
																configForm.totalizator_ref ||
																	"",
															),
												) || null
									}
									onChange={(opt) =>
										updateConfigForm(
											"totalizator_ref",
											opt.value || null,
										)
									}
									options={referenceOptions}
									menuPortalTarget={document.body}
									menuPosition="fixed"
								/>
							</div>
						</div>
					</div>
				) : !indicatorPeriodicity ? (
					<div
						style={{
							textAlign: "center",
							color: "#6c757d",
							padding: "40px 0",
							fontSize: 13,
						}}
					>
						Selecione a periodicidade do indicador antes de adicionar
						variáveis.
					</div>
				) : loading ? (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							padding: "40px 0",
							gap: 8,
						}}
					>
						<span
							className="spinner-border spinner-border-sm"
							style={{ marginRight: 10 }}
						/>
						Carregando...
					</div>
				) : variables.length === 0 ? (
					<div
						style={{
							textAlign: "center",
							color: "#6c757d",
							padding: "40px 0",
						}}
					>
						Nenhuma variável cadastrada. Crie variáveis em "Lançar
						Variáveis".
					</div>
				) : (
					<div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
						{variables.map((variable) => {
							const isAdded = existingVariableIds.includes(
								variable.id,
							);
							return (
								<button
									key={variable.id}
									type="button"
									disabled={isAdded}
									onClick={() => handleSelectVariable(variable)}
									style={{
										width: "calc(33.333% - 8px)",
										minWidth: 180,
										borderRadius: 8,
										border: isAdded
											? "1px solid #22c55e"
											: "1px solid #dee2e6",
										backgroundColor: isAdded
											? "rgba(34,197,94,0.05)"
											: "#fff",
										padding: "10px 14px",
										textAlign: "left",
										cursor: isAdded ? "default" : "pointer",
									}}
								>
									<div style={{ marginBottom: 4 }}>
										<span
											style={{
												fontSize: 13,
												fontWeight: 600,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
											title={variable.name}
										>
											{variable.name}
										</span>
									</div>
									<div
										style={{
											fontSize: 12,
											color: "#6c757d",
										}}
									>
										{variable.quantifier || "Nenhum"} ·{" "}
										{variable.periodicity}
									</div>
									{isAdded && (
										<div
											style={{
												fontSize: 11,
												color: "#22c55e",
												marginTop: 2,
											}}
										>
											✓ Já adicionada
										</div>
									)}
								</button>
							);
						})}
					</div>
				)}
			</ModalBody>
			<ModalFooter
				style={{
					display: "flex",
					justifyContent: "space-between",
					gap: 8,
				}}
			>
				{selectedVariable ? (
					<>
						<Button
							type="button"
							style={{
								background: "none",
								padding: 10,
								color: "black",
							}}
							onClick={handleBackToList}
							disabled={saving}
						>
							Voltar
						</Button>
						<div style={{ display: "flex", gap: 8 }}>
							<Button
								type="button"
								style={{
									background: "none",
									padding: 10,
									color: "black",
								}}
								onClick={handleClose}
								disabled={saving}
							>
								Cancelar
							</Button>
							<Button
								type="button"
								disabled={saving || !isFormValid}
								onClick={handleSave}
								style={{
									background: "none",
									color: "black",
									display: "flex",
									alignItems: "center",
									gap: 4,
									padding: 10,
									opacity: saving || !isFormValid ? 0.5 : 1,
									cursor:
										saving || !isFormValid
											? "not-allowed"
											: "pointer",
								}}
							>
								{saving ? (
									<span
										className="spinner-border spinner-border-sm"
										style={{ marginRight: 6 }}
									/>
								) : (
									<FloppyDisk size={16} />
								)}
								Salvar
							</Button>
						</div>
					</>
				) : (
					<Button
						type="button"
						style={{
							background: "none",
							padding: 10,
							color: "black",
							marginLeft: "auto",
						}}
						onClick={handleClose}
					>
						<SignOut size={16} /> Fechar
					</Button>
				)}
			</ModalFooter>
		</Modal>
	);
};

export default AddVariableModal;
